import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Clock, Code, ChevronLeft, ChevronRight, Send, CheckCircle,
  AlertTriangle, Play, Building2, Loader, ArrowLeft, Trophy,
  FileText, Terminal, AlertCircle, Timer, Maximize
} from 'lucide-react';
import Editor from '@monaco-editor/react';
import axios from 'axios';
import { toast } from 'sonner';
import { loginUrlWithRedirect } from '../utils/urlConfig';

const API = process.env.REACT_APP_BACKEND_URL;

const difficultyColors = {
  easy: 'bg-green-500/20 text-green-400 border-green-500/30',
  medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  hard: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const HiringChallengeAssessmentPage = () => {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [currentProblemIdx, setCurrentProblemIdx] = useState(0);
  const [code, setCode] = useState('// Write your code here\n');
  const [language, setLanguage] = useState('python');
  const [submitting, setSubmitting] = useState(false);
  const [submissions, setSubmissions] = useState({});
  const [remainingTime, setRemainingTime] = useState(null);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  
  const token = localStorage.getItem('codearena_token');
  
  useEffect(() => {
    if (!token) {
      toast.error('Please login to access the assessment');
      navigate(loginUrlWithRedirect('/codearena/auth'), { replace: true });
      return;
    }
    
    startAssessment();
  }, [challengeId, token]);
  
  // Timer effect
  useEffect(() => {
    if (!remainingTime || remainingTime <= 0) return;
    
    const timer = setInterval(() => {
      setRemainingTime(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          toast.warning('Time is up! Auto-submitting...');
          handleFinalSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 60000); // Update every minute
    
    return () => clearInterval(timer);
  }, [remainingTime]);
  
  const startAssessment = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/codearena/hiring-challenges/${challengeId}/start`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAssessment(res.data);
      setRemainingTime(res.data.remaining_minutes);
      
      // Load any existing submissions
      const existingSubs = {};
      res.data.submissions?.forEach(sub => {
        existingSubs[sub.problem_id] = sub;
      });
      setSubmissions(existingSubs);
      
      // Set default code template based on language
      setCode(getCodeTemplate(language));
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to start assessment';
      toast.error(errorMsg);
      
      if (err.response?.status === 403) {
        // Not registered - redirect to challenge details
        navigate(`/codearena/hiring-challenges/${challengeId}`);
      } else if (err.response?.status === 400) {
        // Challenge not live or ended
        navigate(`/codearena/hiring-challenges/${challengeId}`);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const getCodeTemplate = (lang) => {
    const templates = {
      python: '# Write your solution here\n\ndef solution():\n    pass\n',
      javascript: '// Write your solution here\n\nfunction solution() {\n    \n}\n',
      java: '// Write your solution here\n\npublic class Solution {\n    public static void main(String[] args) {\n        \n    }\n}\n',
      cpp: '// Write your solution here\n#include <iostream>\nusing namespace std;\n\nint main() {\n    \n    return 0;\n}\n'
    };
    return templates[lang] || templates.python;
  };
  
  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    // Only change code if it's still the default template
    if (code === getCodeTemplate(language)) {
      setCode(getCodeTemplate(newLang));
    }
  };
  
  const handleSubmitProblem = async () => {
    if (!code.trim()) {
      toast.error('Please write some code before submitting');
      return;
    }
    
    const problem = assessment.problems[currentProblemIdx];
    
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/api/codearena/hiring-challenges/${challengeId}/submit`,
        {
          problem_id: problem.id,
          code: code,
          language: language
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local submissions
      setSubmissions(prev => ({
        ...prev,
        [problem.id]: res.data
      }));
      
      if (res.data.all_passed) {
        toast.success(`All test cases passed! +${res.data.points_earned} points`);
      } else {
        toast.warning(`${res.data.passed_count}/${res.data.total_tests} test cases passed`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleFinalSubmit = async () => {
    toast.success('Assessment completed! Redirecting to results...');
    navigate(`/codearena/hiring-challenges/${challengeId}`);
  };
  
  const formatTime = (minutes) => {
    if (!minutes || minutes <= 0) return '00:00';
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Loader className="animate-spin text-orange-400 mx-auto mb-4" size={48} />
          <p className="text-gray-400">Loading assessment...</p>
        </div>
      </div>
    );
  }
  
  if (!assessment) return null;
  
  const currentProblem = assessment.problems[currentProblemIdx];
  const problemSubmission = submissions[currentProblem?.id];
  const solvedCount = Object.values(submissions).filter(s => s.all_passed).length;
  
  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      {/* Header */}
      <header className="bg-[#0a0a0a] border-b border-white/10 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to={`/codearena/hiring-challenges/${challengeId}`}
              className="p-2 hover:bg-white/5 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-400" />
            </Link>
            <div>
              <h1 className="font-bold text-lg text-white">{assessment.title}</h1>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Building2 size={14} />
                <span>{assessment.company_name}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Progress */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/20 rounded-lg">
              <Trophy size={16} className="text-green-400" />
              <span className="text-green-400 font-medium">
                {solvedCount}/{assessment.problems.length} Solved
              </span>
            </div>
            
            {/* Timer */}
            {remainingTime !== null && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                remainingTime <= 10 
                  ? 'bg-red-500/20 border border-red-500/30 text-red-400' 
                  : 'bg-amber-500/10 border border-amber-500/20 text-amber-400'
              }`}>
                <Timer size={16} />
                <span className="font-mono font-medium">{formatTime(remainingTime)}</span>
              </div>
            )}
            
            {/* Final Submit */}
            <button
              onClick={() => setShowSubmitConfirm(true)}
              className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-emerald-700 transition-all flex items-center gap-2"
              data-testid="finish-assessment-btn"
            >
              <CheckCircle size={18} />
              Finish Assessment
            </button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Problem Panel */}
        <div className="w-1/2 border-r border-white/10 flex flex-col">
          {/* Problem Tabs */}
          <div className="bg-[#0a0a0a] border-b border-white/10 px-4 py-2 flex items-center gap-2 overflow-x-auto">
            {assessment.problems.map((p, idx) => {
              const isSolved = submissions[p.id]?.all_passed;
              const hasSubmission = !!submissions[p.id];
              
              return (
                <button
                  key={p.id}
                  onClick={() => setCurrentProblemIdx(idx)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                    idx === currentProblemIdx
                      ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                      : isSolved
                        ? 'bg-green-500/10 text-green-400 hover:bg-green-500/20'
                        : hasSubmission
                          ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
                          : 'text-gray-400 hover:bg-white/5'
                  }`}
                  data-testid={`problem-tab-${idx}`}
                >
                  {isSolved && <CheckCircle size={14} />}
                  Problem {idx + 1}
                </button>
              );
            })}
          </div>
          
          {/* Problem Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {currentProblem && (
              <div className="space-y-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-xl font-bold text-white">{currentProblem.title}</h2>
                    <span className={`px-2 py-0.5 rounded text-xs border ${difficultyColors[currentProblem.difficulty]}`}>
                      {currentProblem.difficulty}
                    </span>
                    <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs">
                      {currentProblem.points} pts
                    </span>
                  </div>
                </div>
                
                <div className="prose prose-invert max-w-none">
                  <div className="text-gray-300 whitespace-pre-wrap">{currentProblem.description}</div>
                </div>
                
                {currentProblem.constraints && (
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                      <AlertCircle size={16} className="text-amber-400" />
                      Constraints
                    </h4>
                    <p className="text-gray-400 text-sm whitespace-pre-wrap">{currentProblem.constraints}</p>
                  </div>
                )}
                
                {currentProblem.examples?.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-white">Examples</h4>
                    {currentProblem.examples.map((ex, idx) => (
                      <div key={idx} className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Input</p>
                            <code className="block bg-black/30 p-2 rounded text-green-400 text-sm font-mono">
                              {ex.input}
                            </code>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Output</p>
                            <code className="block bg-black/30 p-2 rounded text-cyan-400 text-sm font-mono">
                              {ex.output}
                            </code>
                          </div>
                        </div>
                        {ex.explanation && (
                          <p className="text-gray-400 text-sm mt-2">{ex.explanation}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Submission Status */}
                {problemSubmission && (
                  <div className={`rounded-xl p-4 border ${
                    problemSubmission.all_passed 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : 'bg-amber-500/10 border-amber-500/30'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {problemSubmission.all_passed ? (
                          <CheckCircle className="text-green-400" size={20} />
                        ) : (
                          <AlertTriangle className="text-amber-400" size={20} />
                        )}
                        <span className={problemSubmission.all_passed ? 'text-green-400' : 'text-amber-400'}>
                          {problemSubmission.passed_count}/{problemSubmission.total_tests} Test Cases Passed
                        </span>
                      </div>
                      <span className="text-purple-400 font-semibold">
                        +{problemSubmission.points_earned} pts
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Code Editor Panel */}
        <div className="w-1/2 flex flex-col">
          {/* Editor Header */}
          <div className="bg-[#0a0a0a] border-b border-white/10 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Code size={18} className="text-gray-400" />
              <select
                value={language}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="bg-transparent text-gray-300 text-sm focus:outline-none cursor-pointer"
                data-testid="language-select"
              >
                <option value="python" className="bg-[#0a0a0a]">Python</option>
                <option value="javascript" className="bg-[#0a0a0a]">JavaScript</option>
                <option value="java" className="bg-[#0a0a0a]">Java</option>
                <option value="cpp" className="bg-[#0a0a0a]">C++</option>
              </select>
            </div>
            
            <button
              onClick={handleSubmitProblem}
              disabled={submitting}
              className="px-4 py-1.5 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg text-sm font-medium hover:from-orange-600 hover:to-red-700 transition-all disabled:opacity-50 flex items-center gap-2"
              data-testid="submit-solution-btn"
            >
              {submitting ? (
                <><Loader className="animate-spin" size={14} /> Running...</>
              ) : (
                <><Send size={14} /> Submit Solution</>
              )}
            </button>
          </div>
          
          {/* Monaco Editor */}
          <div className="flex-1">
            <Editor
              height="100%"
              language={language === 'cpp' ? 'cpp' : language}
              value={code}
              onChange={(value) => setCode(value || '')}
              theme="vs-dark"
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
                padding: { top: 16 }
              }}
            />
          </div>
        </div>
      </div>
      
      {/* Finish Confirmation Modal */}
      {showSubmitConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0a0a0a] rounded-2xl max-w-md w-full border border-white/10">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-green-400" size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Finish Assessment?</h3>
              <p className="text-gray-400 mb-4">
                You have solved {solvedCount} out of {assessment.problems.length} problems.
                Are you sure you want to finish?
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowSubmitConfirm(false)}
                  className="flex-1 py-2 border border-white/10 text-gray-400 rounded-lg hover:bg-white/5"
                >
                  Continue Working
                </button>
                <button
                  onClick={handleFinalSubmit}
                  className="flex-1 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-medium"
                  data-testid="confirm-finish-btn"
                >
                  Finish
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HiringChallengeAssessmentPage;
